import React, { useState, useRef } from 'react';
import Draggable from 'react-draggable';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'firebase/auth';
import { signOutUser, updateUserProfile, changeUserPassword } from '../../services/firebase';
import { sdkStorageProvider } from '../../services/storageProvider';

interface UserProfileModalProps {
    show: boolean;
    onClose: () => void;
    user: User | null;
    onSignOut: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
    show,
    onClose,
    user,
    onSignOut
}) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'billing'>('profile');
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editDisplayName, setEditDisplayName] = useState(user?.displayName || '');
    const [editPhotoURL, setEditPhotoURL] = useState(user?.photoURL || '');
    const [saveLoading, setSaveLoading] = useState(false);
    const [storageStats, setStorageStats] = useState<{totalBytes: number; presentationCount: number; formattedSize: string} | null>(null);
    const [loadingStorage, setLoadingStorage] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
    const [isPrivacySettings, setIsPrivacySettings] = useState(false);
    const [emailVisible, setEmailVisible] = useState(true);
    const [profilePublic, setProfilePublic] = useState(false);
    const [allowNotifications, setAllowNotifications] = useState(true);
    const [privacyLoading, setPrivacyLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSignOut = async () => {
        setLoading(true);
        try {
            await signOutUser();
            onSignOut();
            onClose();
        } catch (error) {
            console.error('Error signing out:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        setSaveLoading(true);
        try {
            await updateUserProfile(editDisplayName || undefined, editPhotoURL || undefined);
            setIsEditing(false);
            // Force parent component to refresh user data
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Failed to update profile. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditDisplayName(user?.displayName || '');
        setEditPhotoURL(user?.photoURL || '');
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Convert to base64 for now (in production, upload to Firebase Storage)
            const reader = new FileReader();
            reader.onload = (e) => {
                setEditPhotoURL(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const loadStorageStats = async () => {
        if (!user?.uid || loadingStorage) return;
        
        setLoadingStorage(true);
        try {
            const stats = await sdkStorageProvider.getUserStorageStats();
            setStorageStats(stats);
        } catch (error) {
            console.error('Error loading storage stats:', error);
            setStorageStats({
                totalBytes: 0,
                presentationCount: 0,
                formattedSize: '0 Bytes'
            });
        } finally {
            setLoadingStorage(false);
        }
    };

    // Load storage stats when account tab is viewed
    React.useEffect(() => {
        if (activeTab === 'account' && !storageStats && user?.uid) {
            loadStorageStats();
        }
    }, [activeTab, user?.uid]);

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            alert('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 6) {
            alert('Password must be at least 6 characters long');
            return;
        }
        
        setPasswordChangeLoading(true);
        try {
            await changeUserPassword(currentPassword, newPassword);
            alert('✅ Password changed successfully!');
            setIsChangingPassword(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            alert(`❌ ${error.message}`);
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    const handleCancelPasswordChange = () => {
        setIsChangingPassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleSavePrivacySettings = async () => {
        setPrivacyLoading(true);
        try {
            // In a real app, these would be saved to Firestore user preferences
            const privacySettings = {
                emailVisible,
                profilePublic,
                allowNotifications,
                updatedAt: new Date().toISOString()
            };
            
            // For now, just save to localStorage as a demo
            localStorage.setItem(`privacy_${user?.uid}`, JSON.stringify(privacySettings));
            
            alert('✅ Privacy settings saved successfully!');
            setIsPrivacySettings(false);
        } catch (error) {
            alert('❌ Failed to save privacy settings');
            console.error('Privacy settings error:', error);
        } finally {
            setPrivacyLoading(false);
        }
    };

    const handleCancelPrivacySettings = () => {
        setIsPrivacySettings(false);
        // Reset to previous values (could load from saved settings)
    };

    // Load privacy settings when component mounts
    React.useEffect(() => {
        if (user?.uid) {
            const saved = localStorage.getItem(`privacy_${user.uid}`);
            if (saved) {
                try {
                    const settings = JSON.parse(saved);
                    setEmailVisible(settings.emailVisible ?? true);
                    setProfilePublic(settings.profilePublic ?? false);
                    setAllowNotifications(settings.allowNotifications ?? true);
                } catch (error) {
                    console.error('Error loading privacy settings:', error);
                }
            }
        }
    }, [user?.uid]);

    if (!show || !user) return null;

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
        },
        modal: {
            background: `
                linear-gradient(135deg, 
                  rgba(15, 15, 35, 0.95) 0%,
                  rgba(25, 15, 45, 0.9) 25%,
                  rgba(35, 25, 55, 0.9) 50%,
                  rgba(25, 15, 45, 0.9) 75%,
                  rgba(15, 15, 35, 0.95) 100%
                )
            `,
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            width: '520px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            overflow: 'hidden',
            color: '#fff',
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            position: 'relative' as const,
        },
        header: {
            cursor: 'move',
            padding: '24px 24px 8px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            textAlign: 'center' as const,
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)',
            position: 'relative' as const,
        },
        title: {
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #E6E6FA 0%, #DDA0DD 50%, #DA70D6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '0.5px',
            position: 'relative' as const,
        },
        closeButton: {
            position: 'absolute' as const,
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '18px',
            cursor: 'pointer',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            zIndex: 20,
        },
        tabs: {
            display: 'flex',
            background: 'linear-gradient(135deg, rgba(25, 15, 45, 0.6) 0%, rgba(35, 25, 55, 0.6) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '4px',
            margin: '0 24px',
            borderRadius: '12px',
            marginBottom: '16px',
        },
        tab: {
            flex: 1,
            padding: '10px 16px',
            background: 'none',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            letterSpacing: '0.3px',
        },
        activeTab: {
            color: '#fff',
            background: 'linear-gradient(135deg, rgba(147, 112, 219, 0.3) 0%, rgba(138, 43, 226, 0.2) 100%)',
            boxShadow: '0 2px 12px rgba(147, 112, 219, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            textShadow: '0 0 8px rgba(147, 112, 219, 0.6)',
        },
        content: {
            padding: '24px',
            maxHeight: '450px',
            overflowY: 'auto' as const,
        },
        userHeader: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(147, 112, 219, 0.1) 0%, rgba(138, 43, 226, 0.05) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(147, 112, 219, 0.2)',
            position: 'relative' as const,
        },
        avatar: {
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            marginRight: '20px',
            backgroundColor: '#4ECDC4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#000',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 16px rgba(78, 205, 196, 0.3)',
        },
        userInfo: {
            flex: 1,
        },
        userName: {
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '6px',
            background: 'linear-gradient(135deg, #E6E6FA 0%, #DDA0DD 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        userEmail: {
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '6px',
            letterSpacing: '0.3px',
        },
        userStatus: {
            fontSize: '12px',
            color: '#4ECDC4',
            fontWeight: '600',
            textShadow: '0 0 8px rgba(78, 205, 196, 0.5)',
        },
        section: {
            marginBottom: '24px',
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: '700',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #E6E6FA 0%, #DDA0DD 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '0.4px',
        },
        infoRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
        infoLabel: {
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.6)',
            letterSpacing: '0.3px',
        },
        infoValue: {
            fontSize: '14px',
            color: '#fff',
            fontWeight: '600',
            letterSpacing: '0.2px',
        },
        button: {
            padding: '12px 18px',
            background: 'linear-gradient(135deg, rgba(147, 112, 219, 0.2) 0%, rgba(138, 43, 226, 0.15) 100%)',
            border: '1px solid rgba(147, 112, 219, 0.3)',
            borderRadius: '12px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            width: '100%',
            marginBottom: '10px',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            letterSpacing: '0.3px',
            textShadow: '0 0 8px rgba(147, 112, 219, 0.4)',
            boxShadow: '0 4px 12px rgba(147, 112, 219, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        },
        dangerButton: {
            background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(220, 53, 69, 0.15) 100%)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            textShadow: '0 0 8px rgba(220, 53, 69, 0.4)',
            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        },
        secondaryButton: {
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            textShadow: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        },
        disabledButton: {
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.02) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            cursor: 'not-allowed',
            opacity: 0.5,
            textShadow: 'none',
            boxShadow: 'none',
        },
        badge: {
            padding: '4px 10px',
            background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2) 0%, rgba(78, 205, 196, 0.15) 100%)',
            border: '1px solid rgba(78, 205, 196, 0.3)',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            marginLeft: '8px',
            textShadow: '0 0 8px rgba(78, 205, 196, 0.4)',
            letterSpacing: '0.2px',
        },
        comingSoon: {
            color: '#DDA0DD',
            fontSize: '11px',
            fontStyle: 'italic',
            fontWeight: '500',
            textShadow: '0 0 6px rgba(221, 160, 221, 0.4)',
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '12px',
            backdropFilter: 'blur(10px)',
            letterSpacing: '0.3px',
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
        },
        editProfileSection: {
            background: 'linear-gradient(135deg, rgba(147, 112, 219, 0.05) 0%, rgba(138, 43, 226, 0.02) 100%)',
            border: '1px solid rgba(147, 112, 219, 0.15)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
        },
        photoUploadArea: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
        },
        editButtons: {
            display: 'flex',
            gap: '12px',
            marginTop: '16px',
        },
        saveButton: {
            background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2) 0%, rgba(78, 205, 196, 0.15) 100%)',
            border: '1px solid rgba(78, 205, 196, 0.3)',
            textShadow: '0 0 8px rgba(78, 205, 196, 0.4)',
            boxShadow: '0 4px 12px rgba(78, 205, 196, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        },
        toggleContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
        toggleSwitch: {
            position: 'relative' as const,
            width: '48px',
            height: '24px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
        },
        toggleSwitchActive: {
            backgroundColor: 'rgba(78, 205, 196, 0.3)',
            border: '1px solid rgba(78, 205, 196, 0.5)',
            boxShadow: '0 0 12px rgba(78, 205, 196, 0.3)',
        },
        toggleSlider: {
            position: 'absolute' as const,
            top: '2px',
            left: '2px',
            width: '18px',
            height: '18px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        },
        toggleSliderActive: {
            transform: 'translateX(24px)',
            backgroundColor: '#4ECDC4',
        },
    };

    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const displayName = user.displayName || user.email?.split('@')[0] || 'User';
    const initials = getInitials(displayName);
    const joinDate = user.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'Unknown';
    const lastSignIn = user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'Unknown';

    return (
        <AnimatePresence>
            <div style={styles.overlay}>
                <Draggable handle=".drag-handle" bounds="parent">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: -20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={styles.modal}
                    >
                        {/* Cosmic glow effect */}
                        <div style={{
                            position: 'absolute',
                            top: '-50%',
                            left: '-50%',
                            right: '-50%',
                            bottom: '-50%',
                            background: `
                                radial-gradient(circle at 30% 20%, rgba(147, 112, 219, 0.15) 0%, transparent 50%),
                                radial-gradient(circle at 70% 80%, rgba(75, 0, 130, 0.1) 0%, transparent 50%),
                                radial-gradient(circle at 20% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
                            `,
                            pointerEvents: 'none',
                            zIndex: -1,
                        }} />

                        <div style={styles.header} className="drag-handle">
                            {/* Drag indicator dots */}
                            <div style={{
                                position: 'absolute',
                                top: '8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: '4px',
                                pointerEvents: 'none',
                                zIndex: 2,
                            }}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div
                                        key={i}
                                        style={{
                                            width: '3px',
                                            height: '3px',
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        }}
                                    />
                                ))}
                            </div>

                            <motion.h3
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                style={styles.title}
                            >
                                🧑‍💼 Account Profile
                            </motion.h3>
                            
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '13px',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    fontWeight: '400',
                                    letterSpacing: '0.3px',
                                }}
                            >
                                Manage your cosmic journey settings
                            </motion.p>

                            <motion.button
                                whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                                whileTap={{ scale: 0.9 }}
                                style={styles.closeButton} 
                                onClick={onClose}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                            >
                                ✕
                            </motion.button>
                        </div>

                    <div style={styles.tabs}>
                        <button
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'profile' ? styles.activeTab : {})
                            }}
                            onClick={() => setActiveTab('profile')}
                        >
                            👤 Profile
                        </button>
                        <button
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'account' ? styles.activeTab : {})
                            }}
                            onClick={() => setActiveTab('account')}
                        >
                            ⚙️ Account
                        </button>
                        <button
                            style={{
                                ...styles.tab,
                                ...(activeTab === 'billing' ? styles.activeTab : {})
                            }}
                            onClick={() => setActiveTab('billing')}
                        >
                            💳 Billing
                        </button>
                    </div>

                    <div style={styles.content}>
                        {/* User Header - Always Visible */}
                        <div style={styles.userHeader}>
                            {(isEditing ? editPhotoURL : user.photoURL) ? (
                                <img 
                                    src={isEditing ? editPhotoURL : user.photoURL || ''} 
                                    alt="Profile" 
                                    style={{...styles.avatar, fontSize: 'inherit'}}
                                    data-html2canvas-ignore="true"
                                />
                            ) : (
                                <div style={styles.avatar}>
                                    {getInitials(isEditing ? editDisplayName : displayName)}
                                </div>
                            )}
                            <div style={styles.userInfo}>
                                <div style={styles.userName}>
                                    {isEditing ? editDisplayName || 'Unnamed User' : displayName}
                                </div>
                                <div style={styles.userEmail}>{user.email}</div>
                                <div style={styles.userStatus}>
                                    ✅ Authenticated
                                    {user.emailVerified && <span style={styles.badge}>Verified</span>}
                                </div>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'profile' && (
                            <div>
                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>
                                        {isEditing ? 'Edit Profile' : 'Profile Information'}
                                    </h4>
                                    
                                    {isEditing ? (
                                        <div style={styles.editProfileSection}>
                                            <div style={styles.photoUploadArea}>
                                                <div 
                                                    style={{
                                                        ...styles.avatar,
                                                        width: '80px',
                                                        height: '80px',
                                                        cursor: 'pointer',
                                                        border: '2px dashed rgba(147, 112, 219, 0.5)',
                                                        backgroundColor: editPhotoURL ? 'transparent' : '#4ECDC4'
                                                    }}
                                                    onClick={() => fileInputRef.current?.click()}
                                                >
                                                    {editPhotoURL ? (
                                                        <img 
                                                            src={editPhotoURL} 
                                                            alt="Profile Preview" 
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                borderRadius: '50%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    ) : (
                                                        <span style={{fontSize: '32px'}}>📷</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '4px'}}>
                                                        Profile Picture
                                                    </div>
                                                    <div style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)'}}>
                                                        Click to upload or change
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                style={{ display: 'none' }}
                                            />
                                            
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    marginBottom: '6px',
                                                    fontSize: '13px',
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    fontWeight: '600'
                                                }}>
                                                    Display Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editDisplayName}
                                                    onChange={(e) => setEditDisplayName(e.target.value)}
                                                    placeholder="Enter your display name"
                                                    style={styles.input}
                                                />
                                            </div>
                                            
                                            <div style={styles.editButtons}>
                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    style={{
                                                        ...styles.button,
                                                        ...styles.saveButton,
                                                        flex: 1,
                                                        ...(saveLoading ? styles.disabledButton : {})
                                                    }}
                                                    onClick={handleSaveProfile}
                                                    disabled={saveLoading}
                                                >
                                                    {saveLoading ? '🔄 Saving...' : '💾 Save Changes'}
                                                </motion.button>
                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    style={{
                                                        ...styles.button,
                                                        ...styles.secondaryButton,
                                                        flex: 1
                                                    }}
                                                    onClick={handleCancelEdit}
                                                >
                                                    ❌ Cancel
                                                </motion.button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={styles.infoRow}>
                                                <span style={styles.infoLabel}>Display Name</span>
                                                <span style={styles.infoValue}>{displayName}</span>
                                            </div>
                                            <div style={styles.infoRow}>
                                                <span style={styles.infoLabel}>Email Address</span>
                                                <span style={styles.infoValue}>{user.email}</span>
                                            </div>
                                            <div style={styles.infoRow}>
                                                <span style={styles.infoLabel}>Email Verified</span>
                                                <span style={styles.infoValue}>
                                                    {user.emailVerified ? '✅ Yes' : '❌ No'}
                                                </span>
                                            </div>
                                            <div style={styles.infoRow}>
                                                <span style={styles.infoLabel}>User ID</span>
                                                <span style={styles.infoValue}>{user.uid.substring(0, 8)}...</span>
                                            </div>
                                            
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    ...styles.button,
                                                    marginTop: '16px'
                                                }}
                                                onClick={() => setIsEditing(true)}
                                            >
                                                ✏️ Edit Profile
                                            </motion.button>
                                        </>
                                    )}
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Account Activity</h4>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Member Since</span>
                                        <span style={styles.infoValue}>{joinDate}</span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Last Sign In</span>
                                        <span style={styles.infoValue}>{lastSignIn}</span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Provider</span>
                                        <span style={styles.infoValue}>
                                            {user.providerData[0]?.providerId.includes('google') ? '🔗 Google' : '📧 Email'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'account' && (
                            <div>
                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Account Management</h4>
                                    <p style={{color: '#ccc', fontSize: '14px', marginBottom: '16px'}}>
                                        Manage your account settings and preferences.
                                    </p>
                                    

                                    
                                    {user.providerData[0]?.providerId === 'password' ? (
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            style={{...styles.button, ...styles.secondaryButton}}
                                            onClick={() => setIsChangingPassword(true)}
                                        >
                                            🔐 Change Password
                                        </motion.button>
                                    ) : (
                                        <div style={{
                                            ...styles.button,
                                            ...styles.disabledButton,
                                            cursor: 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}>
                                            🔐 Change Password
                                            <span style={{fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)'}}>
                                                (Google Account)
                                            </span>
                                        </div>
                                    )}
                                    
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        style={{...styles.button, ...styles.secondaryButton}}
                                        onClick={() => setIsPrivacySettings(true)}
                                    >
                                        🛡️ Privacy Settings
                                    </motion.button>
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Data & Storage</h4>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Cloud Presentations</span>
                                        <span style={styles.infoValue}>
                                            {storageStats ? `📊 ${storageStats.presentationCount} presentations` : '📊 Loading...'}
                                        </span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Storage Used</span>
                                        <span style={styles.infoValue}>
                                            {loadingStorage ? '📈 Calculating...' : 
                                             storageStats ? `💾 ${storageStats.formattedSize}` : '📈 Click refresh to load'}
                                        </span>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        style={{
                                            ...styles.button,
                                            ...styles.secondaryButton,
                                            marginTop: '12px',
                                            ...(loadingStorage ? styles.disabledButton : {})
                                        }}
                                        onClick={loadStorageStats}
                                        disabled={loadingStorage}
                                    >
                                        {loadingStorage ? '🔄 Refreshing...' : '🔄 Refresh Stats'}
                                    </motion.button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'billing' && (
                            <div>
                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Subscription Plan</h4>
                                    <div style={{
                                        padding: '16px',
                                        backgroundColor: '#2a2a2a',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        textAlign: 'center' as const
                                    }}>
                                        <div style={{fontSize: '18px', fontWeight: '600', marginBottom: '8px'}}>
                                            🆓 Free Plan
                                        </div>
                                        <div style={{color: '#4ECDC4', fontSize: '14px'}}>
                                            Unlimited presentations • Cloud storage • Full features
                                        </div>
                                    </div>
                                    
                                    <p style={{color: '#ccc', fontSize: '14px', textAlign: 'center' as const}}>
                                        🎉 All features are currently free! 
                                        <br />
                                        Premium plans with advanced features coming soon.
                                    </p>
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Payment Methods</h4>
                                    <button 
                                        style={{...styles.button, ...styles.secondaryButton}}
                                        onClick={() => alert('🚧 Payment methods coming with premium plans!')}
                                    >
                                        💳 Add Payment Method
                                        <span style={styles.comingSoon}> (Coming Soon)</span>
                                    </button>
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Billing History</h4>
                                    <div style={{
                                        padding: '16px',
                                        backgroundColor: '#2a2a2a',
                                        borderRadius: '8px',
                                        textAlign: 'center' as const,
                                        color: '#ccc'
                                    }}>
                                        📋 No billing history yet
                                        <br />
                                        <small>All features are currently free</small>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Sign Out Button - Always Visible */}
                        <div style={{...styles.section, borderTop: '1px solid #333', paddingTop: '20px'}}>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                style={{
                                    ...styles.button, 
                                    ...styles.dangerButton,
                                    ...(loading ? styles.disabledButton : {})
                                }}
                                onClick={handleSignOut}
                                disabled={loading}
                            >
                                {loading ? '🔄 Signing Out...' : '🚪 Sign Out'}
                            </motion.button>
                        </div>
                        </div>
                    </motion.div>
                </Draggable>
            </div>

            {/* Password Change Modal */}
            {isChangingPassword && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 20001,
                }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        style={{
                            ...styles.modal,
                            width: '400px',
                            maxWidth: '90vw',
                            maxHeight: 'auto',
                        }}
                    >
                        <div style={{padding: '24px'}}>
                            <h3 style={{
                                ...styles.title,
                                fontSize: '20px',
                                marginBottom: '16px',
                                textAlign: 'center'
                            }}>
                                🔐 Change Password
                            </h3>
                            
                            <div style={{marginBottom: '16px'}}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '13px',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: '600'
                                }}>
                                    Current Password
                                </label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Enter your current password"
                                    style={styles.input}
                                />
                            </div>
                            
                            <div style={{marginBottom: '16px'}}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '13px',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: '600'
                                }}>
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password (min 6 characters)"
                                    style={styles.input}
                                />
                            </div>
                            
                            <div style={{marginBottom: '24px'}}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '13px',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: '600'
                                }}>
                                    Confirm New Password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your new password"
                                    style={styles.input}
                                />
                            </div>
                            
                            <div style={styles.editButtons}>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        ...styles.button,
                                        ...styles.saveButton,
                                        flex: 1,
                                        ...(passwordChangeLoading ? styles.disabledButton : {})
                                    }}
                                    onClick={handleChangePassword}
                                    disabled={passwordChangeLoading}
                                >
                                    {passwordChangeLoading ? '🔄 Changing...' : '🔐 Change Password'}
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        ...styles.button,
                                        ...styles.secondaryButton,
                                        flex: 1
                                    }}
                                    onClick={handleCancelPasswordChange}
                                >
                                    ❌ Cancel
                                </motion.button>
                            </div>
                        </div>
                                         </motion.div>
                 </div>
             )}

            {/* Privacy Settings Modal */}
            {isPrivacySettings && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 20001,
                }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        style={{
                            ...styles.modal,
                            width: '450px',
                            maxWidth: '90vw',
                            maxHeight: 'auto',
                        }}
                    >
                        <div style={{padding: '24px'}}>
                            <h3 style={{
                                ...styles.title,
                                fontSize: '20px',
                                marginBottom: '16px',
                                textAlign: 'center'
                            }}>
                                🛡️ Privacy Settings
                            </h3>
                            
                            <div style={{marginBottom: '24px'}}>
                                <div style={styles.toggleContainer}>
                                    <div>
                                        <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}>
                                            Email Visibility
                                        </div>
                                        <div style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)'}}>
                                            Allow others to see your email address
                                        </div>
                                    </div>
                                    <div 
                                        style={{
                                            ...styles.toggleSwitch,
                                            ...(emailVisible ? styles.toggleSwitchActive : {})
                                        }}
                                        onClick={() => setEmailVisible(!emailVisible)}
                                    >
                                        <div style={{
                                            ...styles.toggleSlider,
                                            ...(emailVisible ? styles.toggleSliderActive : {})
                                        }} />
                                    </div>
                                </div>
                                
                                <div style={styles.toggleContainer}>
                                    <div>
                                        <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}>
                                            Public Profile
                                        </div>
                                        <div style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)'}}>
                                            Make your profile visible to other users
                                        </div>
                                    </div>
                                    <div 
                                        style={{
                                            ...styles.toggleSwitch,
                                            ...(profilePublic ? styles.toggleSwitchActive : {})
                                        }}
                                        onClick={() => setProfilePublic(!profilePublic)}
                                    >
                                        <div style={{
                                            ...styles.toggleSlider,
                                            ...(profilePublic ? styles.toggleSliderActive : {})
                                        }} />
                                    </div>
                                </div>
                                
                                <div style={styles.toggleContainer}>
                                    <div>
                                        <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}>
                                            Email Notifications
                                        </div>
                                        <div style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)'}}>
                                            Receive notifications about your presentations
                                        </div>
                                    </div>
                                    <div 
                                        style={{
                                            ...styles.toggleSwitch,
                                            ...(allowNotifications ? styles.toggleSwitchActive : {})
                                        }}
                                        onClick={() => setAllowNotifications(!allowNotifications)}
                                    >
                                        <div style={{
                                            ...styles.toggleSlider,
                                            ...(allowNotifications ? styles.toggleSliderActive : {})
                                        }} />
                                    </div>
                                </div>
                            </div>
                            
                            <div style={styles.editButtons}>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        ...styles.button,
                                        ...styles.saveButton,
                                        flex: 1,
                                        ...(privacyLoading ? styles.disabledButton : {})
                                    }}
                                    onClick={handleSavePrivacySettings}
                                    disabled={privacyLoading}
                                >
                                    {privacyLoading ? '🔄 Saving...' : '💾 Save Settings'}
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        ...styles.button,
                                        ...styles.secondaryButton,
                                        flex: 1
                                    }}
                                    onClick={handleCancelPrivacySettings}
                                >
                                    ❌ Cancel
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}; 